import { FC } from 'react';
import { Environment, fetchQuery, graphql } from 'react-relay';
import { sessionStorage } from 'dibs-browser-storage';
import { getBuyerId } from 'dibs-cookie-jar';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import { DESKTOP } from '../../../../exports/deviceTypeEnums';
import { importModuleAndRenderNotification } from '../helpers/renderHelpers';
import { getValidItems } from './helpers/getValidItems';
import { AUCTION_WON_NOTIFICATION_DISPLAY_KEY } from './constants';
import { auctionWonRuleQuery } from './__generated__/auctionWonRuleQuery.graphql';

const importAuctionWon = async (): Promise<FC> =>
    (
        await import(
            /* webpackChunkName: "AuctionWon" */
            './AuctionWon'
        )
    ).AuctionWon;

type RuleArgs = {
    relayEnvironment: Environment;
};

export const auctionWonRule = async ({ relayEnvironment }: RuleArgs): Promise<boolean> => {
    if (sessionStorage.getItem(AUCTION_WON_NOTIFICATION_DISPLAY_KEY)) {
        return false;
    }

    const userId = getBuyerId(document.cookie);

    const data = await fetchQuery<auctionWonRuleQuery>(
        relayEnvironment,
        graphql`
            query auctionWonRuleQuery($userId: String!, $hasUserId: Boolean!) {
                viewer {
                    youWonAuctionNotification: featureFlag(feature: "youWonAuctionNotification")
                    user(userId: $userId) @include(if: $hasUserId) {
                        activityFeed(elementTypes: [BUYER_AUCTION_WON], elementStatuses: [ACTIVE]) {
                            edges {
                                node {
                                    expirationDate
                                    anchors {
                                        sourceType
                                        source {
                                            ... on Item {
                                                lastTransaction(buyerId: $userId) {
                                                    isAuctionWonAndPending
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
        { userId, hasUserId: !!userId }
    ).toPromise();

    const { viewer } = data || {};

    return (
        !!viewer?.youWonAuctionNotification && getValidItems(viewer?.user?.activityFeed).length > 0
    );
};

export const auctionWonAction = () => async (): Promise<void> => {
    importModuleAndRenderNotification({
        importFunc: importAuctionWon,
    });
};

export default {
    name: 'auctionWon',
    rule: auctionWonRule,
    action: auctionWonAction,
    priority: 550,
    blockedPageTypes: [PAGE_TYPE.CHECKOUT, PAGE_TYPE.CHECKOUT_CONFIRMATION, PAGE_TYPE.CART],
    deviceTypes: [DESKTOP],
};
